import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import HttpManager from "../../services/HttpManager";
import { serverError } from "../modals/payments/register/registerPaymentModal";
import TiraDeMeses from "../acuerdos/tiraDeMeses";
import TarjetaAprobacion from "../aprobaciones/tarjetaAprobacion";
import { MIN_MOTIVO } from "../aprobaciones/tipos";
import CursoRegalado2x1, { CursoRegalado } from "./cursoRegalado2x1";
import { EligeRegalo, Pill, dinero } from "./tarjetasPromo";
import { KIND_DOS_X_UNO, PromoOption, giftOptionsOf, initialGiftKey } from "./tipos";
import { CambioOpcion, CambioPersona, CambioPeticion, CambioPreview, CambioPromoDTO, CambioResultado } from "./cambiarPromocionTipos";

/*
	«Cambiar Promoción» en la ficha (docs/ESPEC_PROMO_EN_CAMINO.md §6, §6.1, §6.2). Una sola ventana, de arriba abajo:
	Tiene Ahora, Promoción Nueva, Qué Va a Pasar (obligatorio, lo calcula el servidor al escoger), Motivo y Confirmar.
	Modal en PC y pantalla completa en celular (fullscreen="sm-down", como Eximir Meses). Caja del plantel, Supervisión de Caja,
	Dirección de Ventas y superadmin; el servidor dice quién puede con `canChange` y vuelve a revisarlo al confirmar.
	Lo que le sube el precio a otra persona sale como «Enviar a Aprobación»: queda Por Aprobar y le llega el botón por WappTeam.
*/

export interface CambiarPromocionProps {
	campusId: number;
	/** La persona de la matrícula (la base si se elige un 2x1). */
	studentId: number;
	enrollmentId: number;
	courseName: string;
	onClose: () => void;
	/** Algo cambió (se aplicó, quedó En Camino o Por Aprobar): recargar el Estado de Cuenta. */
	onChanged: () => void;
}

const http = HttpManager.getInstance();

const llave = (o: CambioOpcion) => (o.promotionId === null ? "lista" : "p" + o.promotionId);
const fecha = (d: string | null) => (d ? d.slice(8, 10) + "/" + d.slice(5, 7) + "/" + d.slice(0, 4) : "");

const tituloSeccion: React.CSSProperties = { fontWeight: 700, fontSize: 15, margin: "16px 0 6px" };
const caja: React.CSSProperties = { border: "1px solid var(--wa-filo)", borderRadius: 8, padding: "8px 10px", marginTop: 8, background: "var(--wa-superficie)" };
const avisoOro: React.CSSProperties = { border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", color: "var(--wa-oro)", borderRadius: 8, padding: "8px 10px", marginTop: 8, fontWeight: 600 };

/** La opción como la entiende EligeRegalo (solo usa enrollmentId, promotionId, giftValue y giftOptions). */
const comoPromoOption = (o: CambioOpcion, enrollmentId: number): PromoOption => ({
	enrollmentId, promotionId: o.promotionId || 0, name: o.name, kind: o.kind || "", giftOptions: o.giftOptions, giftValue: o.giftValue, giftOptionKey: o.giftOptionKey,
} as unknown as PromoOption);

const TarjetaPersona: React.FC<{ p: CambioPersona; sym: string }> = ({ p, sym }) => (
	<div style={caja}>
		<div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
			<b>{p.name}</b>{p.isThis ? <Pill t="Esta Matrícula" tone="acento" /> : null}
		</div>
		{p.courseName ? <div style={{ color: "var(--wa-apagado)", fontSize: 13 }}>{p.courseName}</div> : null}
		{p.lines.map((l, i) => (
			<div key={"l" + i} style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 4, color: l.up ? "var(--wa-rojo)" : "var(--wa-tinta)", fontWeight: l.up ? 600 : 400 }}>
				<span style={{ minWidth: 0 }}>{l.text}</span>
				{l.amount !== null && l.amount !== undefined && Math.abs(Number(l.amount)) > 0.004 ? <span style={{ whiteSpace: "nowrap", fontWeight: 700 }}>{(l.up ? "+ " : "") + dinero(sym, Math.abs(Number(l.amount)))}</span> : null}
			</div>
		))}
		{p.monthly.length ? (
			<table style={{ width: "100%", marginTop: 8, fontSize: 13, borderCollapse: "collapse" }}>
				<thead>
					<tr style={{ color: "var(--wa-apagado)", textAlign: "left" }}>
						<th style={{ fontWeight: 600, padding: "2px 4px 2px 0" }}>Mes</th><th style={{ fontWeight: 600, padding: "2px 4px" }}>Desde</th>
						<th style={{ fontWeight: 600, padding: "2px 4px", textAlign: "right" }}>Antes</th><th style={{ fontWeight: 600, padding: "2px 0 2px 4px", textAlign: "right" }}>Después</th>
					</tr>
				</thead>
				<tbody>
					{p.monthly.map((m, i) => {
						const sube = Number(m.after) > Number(m.before) + 0.004;
						return (
							<tr key={"m" + i} style={{ borderTop: "1px solid var(--wa-filo)" }}>
								<td style={{ padding: "3px 4px 3px 0" }}>{m.title}</td><td style={{ padding: "3px 4px", whiteSpace: "nowrap" }}>{fecha(m.from)}</td>
								<td style={{ padding: "3px 4px", textAlign: "right", whiteSpace: "nowrap" }}>{dinero(sym, m.before)}</td>
								<td style={{ padding: "3px 0 3px 4px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 700, color: sube ? "var(--wa-rojo)" : "var(--wa-tinta)" }}>{dinero(sym, m.after)}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		) : null}
	</div>
);

const CambiarPromocion: React.FC<CambiarPromocionProps> = ({ campusId, studentId, enrollmentId, courseName, onClose, onChanged }) => {
	const [data, setData] = useState<CambioPromoDTO | null>(null);
	const [elegida, setElegida] = useState<string | null>(null);
	const [regalo, setRegalo] = useState<CursoRegalado | null>(null);
	const [problemas, setProblemas] = useState<string[] | null>(null);
	const [giftKey, setGiftKey] = useState<string | null>(null);
	const [previa, setPrevia] = useState<CambioPreview | null>(null);
	const [calculando, setCalculando] = useState<boolean>(false);
	const [errorPrevia, setErrorPrevia] = useState<string | null>(null);
	const [motivo, setMotivo] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [resultado, setResultado] = useState<CambioResultado | null>(null);
	/** Sube cada vez que hay que volver a calcular Qué Va a Pasar (409: la cuenta cambió). */
	const [intento, setIntento] = useState<number>(0);
	const turno = useRef<number>(0);

	const cargar = useCallback(async () => {
		try {
			const r = await http.enrollmentService.getCambioPromo(campusId, enrollmentId);
			setData(r.data);
		} catch (e) {
			setError(serverError(e));
		}
	}, [campusId, enrollmentId]);
	useEffect(() => { cargar(); }, [cargar]);

	const sym = data?.currencySymbol || "";
	const opcion = data ? data.options.find((o) => llave(o) === elegida) || null : null;
	const es2x1 = !!opcion && opcion.kind === KIND_DOS_X_UNO;
	const pseudo = opcion ? comoPromoOption(opcion, enrollmentId) : null;
	const regalos = pseudo ? giftOptionsOf(pseudo) : [];

	const elegir = (o: CambioOpcion) => {
		if (o.blocked || busy) return;
		setElegida(llave(o));
		setRegalo(null);
		setProblemas(null);
		setGiftKey(initialGiftKey(comoPromoOption(o, enrollmentId)));
		setPrevia(null);
		setErrorPrevia(null);
		setError(null);
		setResultado(null);
	};

	const peticion = (): CambioPeticion => ({
		promotionId: opcion ? opcion.promotionId : null,
		...(es2x1 && regalo ? { giftEnrollmentId: regalo.enrollmentId, giftStudentId: regalo.studentId } : {}),
		...(giftKey ? { giftOptionKey: giftKey } : {}),
	});

	// Lo que falta elegir antes de poder calcular Qué Va a Pasar.
	const falta: string | null = !opcion || opcion.blocked ? "x"
		: es2x1 && !regalo ? "Elige el Curso Regalado"
			: es2x1 && (problemas === null || problemas.length > 0) ? "x"
				: regalos.length >= 2 && !giftKey ? "Elige el Regalo" : null;

	useEffect(() => {
		if (falta) { setPrevia(null); return; }
		const mio = ++turno.current;
		setCalculando(true);
		setErrorPrevia(null);
		setPrevia(null);
		http.enrollmentService.previewCambioPromo(campusId, enrollmentId, peticion())
			.then((r) => { if (mio === turno.current) setPrevia(r.data); })
			.catch((e) => { if (mio === turno.current) setErrorPrevia(serverError(e)); })
			.finally(() => { if (mio === turno.current) setCalculando(false); });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [elegida, regalo?.enrollmentId, giftKey, falta, intento, campusId, enrollmentId]);

	const confirmar = async () => {
		if (!previa) return;
		try {
			setBusy(true);
			setError(null);
			const r = await http.enrollmentService.cambiarPromo(campusId, enrollmentId, { ...peticion(), reason: motivo.trim(), previewHash: previa.previewHash });
			setResultado(r.data);
			setElegida(null);
			setRegalo(null);
			setPrevia(null);
			setMotivo("");
			onChanged();
			await cargar();
		} catch (e: any) {
			setError(serverError(e));
			// 409: la cuenta cambió desde Qué Va a Pasar. Se vuelve a calcular y a leer lo que hay; el motivo se queda escrito.
			if (e?.response?.status === 409) { setIntento((n) => n + 1); cargar(); }
		} finally {
			setBusy(false);
		}
	};

	const pendiente = data?.pending.approval && data.pending.approval.status === "pendiente" ? data.pending.approval : null;
	const enCamino = data?.pending.enCamino || null;
	const puedeElegir = !!data && data.canChange && !pendiente;
	const aprobacion = !!previa && previa.needsApproval;
	const listo = !!previa && !previa.blocked && !calculando && motivo.trim().length >= MIN_MOTIVO && !busy;

	return (
		<Modal className="waModulo" show onHide={onClose} size="lg" centered scrollable fullscreen="sm-down" style={{ zIndex: 2000 }}>
			<Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>Cambiar Promoción: {courseName}</Modal.Title></Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 14 }}>
				{data === null ? (error ? <div className="alert alert-danger">{error}</div> : <div className="loader" />) : (
					<>
						{resultado ? <div style={{ ...caja, borderColor: "var(--wa-acento)", background: "var(--wa-acento-suave)", color: "var(--wa-acento-texto)", fontWeight: 600, marginTop: 0 }} role="status">{resultado.text}</div> : null}

						<div style={{ ...tituloSeccion, marginTop: resultado ? 16 : 0 }}>Tiene Ahora</div>
						<div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
							<b>{data.current.promoName || "Sin Promoción"}</b>
							{data.current.status ? <Pill t={data.current.status} tone="acento" /> : null}
						</div>
						{enCamino ? (
							<div style={avisoOro}>
								{enCamino.promoName ? "«" + enCamino.promoName + "» " : ""}{enCamino.statusName}: lleva {dinero(sym, enCamino.paid)} de {dinero(sym, enCamino.target)}, faltan {dinero(sym, enCamino.remaining)}
								{enCamino.dueDate ? ", vence " + fecha(enCamino.dueDate) : ""}
							</div>
						) : null}
						{data.current.strip && data.current.strip.months.length ? (
							<TiraDeMeses meses={data.current.strip.months.map((m) => ({ n: m.n, title: m.title, state: m.state, subTotal: m.subTotal, list: m.listAmount, note: m.note }))} sym={sym} compacta />
						) : null}
						{data.links.map((l, i) => (
							<div key={"lk" + i} style={caja}>
								<div>{l.text}</div>
								{l.people.length ? (
									<div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
										{l.people.map((p) => <Pill key={"lp" + p.studentId} t={p.name + (p.courseName ? ", " + p.courseName : "")} tone="filo" />)}
									</div>
								) : null}
							</div>
						))}
						{pendiente ? (
							<>
								<div style={tituloSeccion}>Por Aprobar</div>
								<TarjetaAprobacion a={pendiente} onCambio={() => { cargar(); onChanged(); }} />
							</>
						) : null}
						{!data.canChange && !pendiente ? <div style={avisoOro}>No puedes cambiar la promoción de esta matrícula.</div> : null}

						{puedeElegir ? (
							<>
								<div style={tituloSeccion}>Promoción Nueva</div>
								{data.options.map((o) => {
									const on = elegida === llave(o);
									const actual = !!data.current.promoName && o.promotionId !== null && o.name === data.current.promoName;
									return (
										<div key={llave(o)}>
											<label style={{ position: "relative", display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 10px", margin: "6px 0", cursor: o.blocked ? "default" : "pointer", whiteSpace: "normal",
												border: "1px solid " + (on ? "var(--wa-acento)" : "var(--wa-filo)"), borderRadius: 8, background: on ? "var(--wa-acento-suave)" : "var(--wa-superficie)", opacity: o.blocked ? 0.7 : 1 }}>
												<input type="radio" name="cambiar-promo" style={{ marginTop: 3 }} checked={on} disabled={!!o.blocked || busy} onChange={() => elegir(o)} />
												<span style={{ minWidth: 0, flex: 1 }}>
													<span style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
														<b>{o.name}</b>
														{o.adelanto ? <Pill t="Adelanto" tone="oro" /> : null}
														{actual ? <Pill t="Actual" tone="filo" /> : null}
													</span>
													{o.blocked ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, marginTop: 2 }}>{o.blocked}</div> : null}
												</span>
											</label>
											{on && es2x1 ? (
												<div style={{ margin: "0 0 8px 24px" }}>
													<CursoRegalado2x1 campusId={campusId} studentId={studentId} baseEnrollmentId={enrollmentId} giftEnrollmentId={regalo?.enrollmentId || null}
														disabled={busy} serverError={serverError} onPick={(v) => { setRegalo(v); setProblemas(null); setPrevia(null); }} onIssues={setProblemas} onError={setError} />
													{problemas && problemas.length ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, marginTop: 4 }}>{problemas.join(" ")}</div> : null}
												</div>
											) : null}
											{on && pseudo && regalos.length >= 2 ? (
												<div style={{ margin: "0 0 8px 24px" }}><EligeRegalo o={pseudo} sym={sym} giftKey={giftKey} disabled={busy} onChange={(k) => { setGiftKey(k); setPrevia(null); }} /></div>
											) : null}
										</div>
									);
								})}

								{opcion && !falta ? (
									<>
										<div style={tituloSeccion}>Qué Va a Pasar</div>
										{calculando ? <div style={{ color: "var(--wa-apagado)" }}>Calculando…</div> : null}
										{errorPrevia ? <div className="alert alert-danger" style={{ marginTop: 6 }}>{errorPrevia}</div> : null}
										{previa ? (
											<>
												{previa.blocked ? <div className="alert alert-danger" style={{ marginTop: 6 }}>{previa.blocked}</div> : null}
												{previa.whenText ? <div style={avisoOro}>{previa.whenText}</div> : null}
												{previa.people.map((p) => <TarjetaPersona key={"pp" + p.studentId + "-" + p.courseName} p={p} sym={sym} />)}
												{previa.totals.length ? (
													<div style={{ ...caja, background: "var(--wa-papel-3)" }}>
														<div style={{ fontWeight: 700, marginBottom: 2 }}>Total</div>
														{previa.totals.map((t) => {
															const partes: string[] = [];
															if (Number(t.addNow) > 0.004) partes.push("se le suman " + dinero(sym, t.addNow));
															if (Math.abs(Number(t.monthlyDelta)) > 0.004) partes.push((t.monthlyDelta > 0 ? "sube " : "baja ") + dinero(sym, Math.abs(Number(t.monthlyDelta))) + " al mes");
															const sube = Number(t.addNow) > 0.004 || Number(t.monthlyDelta) > 0.004;
															return <div key={"t" + t.studentId} style={{ color: sube ? "var(--wa-rojo)" : "var(--wa-tinta)", fontWeight: sube ? 600 : 400 }}><b>{t.name}</b>: {partes.length ? partes.join(", ") : "sin cambio de precio"}</div>;
														})}
													</div>
												) : null}
												{aprobacion ? <div style={avisoOro}>Necesita Aprobación{previa.approvalWhy ? ": " + previa.approvalWhy : ""}</div> : null}

												<div style={tituloSeccion}>Motivo</div>
												<textarea className="form-control" rows={2} style={{ fontSize: 16 }} placeholder="Motivo del Cambio" value={motivo} disabled={busy} onChange={(e) => setMotivo(e.target.value)} />
												<div style={{ fontSize: 12, marginTop: 2, color: motivo.trim().length >= MIN_MOTIVO ? "var(--wa-apagado)" : "var(--wa-oro)" }}>{motivo.trim().length}/{MIN_MOTIVO}</div>
											</>
										) : null}
									</>
								) : null}
							</>
						) : null}
						{error ? <div className="alert alert-danger" style={{ marginTop: 10 }} role="alert">{error}</div> : null}
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cerrar</button>
				{puedeElegir && previa ? (
					<button type="button" className="btn btn-primary" disabled={!listo} onClick={confirmar}>{aprobacion ? "Enviar a Aprobación" : "Confirmar"}</button>
				) : null}
			</Modal.Footer>
		</Modal>
	);
};

export default CambiarPromocion;
